import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Button,
  Group,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openModal } from '@mantine/modals';
import {
  IconPlus,
  IconSearch,
  IconWheelchair,
  IconX,
} from '@tabler/icons-react';

import {
  GetPatientsRequestParams,
  Patient,
  PatientsSortBy,
} from '@interfaces/patients.interface';

import {
  useCountPatientsQuery,
  useGetManyPatientsQuery,
} from '@api/patients.api';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import EthnicitySelector from '@components/ethnicities/EthnicitySelector';
import GenderSelector from '@components/genders/GenderSelector';
import MedicalOrganizationsSelector from '@components/medicalOrganizations/MedicalOrganizationsSelector';
import PathologiesSelector from '@components/pathologies/PathologiesSelector';
import UpsertPatientForm, {
  UPSERT_PATIENT_FORM_MODAL_ID,
} from '@components/patients/UpsertPatientForm';
import { useUpdateUserMutation } from '@api/users.api';

export default function PatientsList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const navigate = useNavigate();

  const canCreatePatients = useUserPermissionGuard('PATIENTS.CREATE');
  const canEditUsers = useUserPermissionGuard('USERS.UPDATE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetPatientsRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'lastName',
    sortOrder: 'asc',
  });

  const [firstNameSearch, setFirstNameSearch, firstNameSearchDebounced] =
    useStateWithDebounce('');
  const [lastNameSearch, setLastNameSearch, lastNameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Patient>>({
    columnAccessor: 'lastName',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as PatientsSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: patientsCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountPatientsQuery({
      firstNameSearch: firstNameSearchDebounced,
      lastNameSearch: lastNameSearchDebounced,
      emailSearch: emailSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyPatientsQuery({
    ...filters,
    firstNameSearch: firstNameSearchDebounced,
    lastNameSearch: lastNameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const [updateUser, { isLoading: isLoadingUpdateUser }] =
    useUpdateUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddPatientClick = useCallback(() => {
    openModal({
      modalId: UPSERT_PATIENT_FORM_MODAL_ID,
      title: 'Registra paziente',
      children: (
        <UpsertPatientForm
          afterSubmit={({ id }) => navigate(`/pazienti/${id}`)}
        />
      ),
      size: '60rem',
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconWheelchair />
        Pazienti {!isLoadingCount ? `(${patientsCount.count})` : ''}
      </Group>
    ),
    [patientsCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreatePatients ? (
        <Button leftSection={<IconPlus />} onClick={handleAddPatientClick}>
          Registra paziente
        </Button>
      ) : (
        <></>
      ),
    [handleAddPatientClick, canCreatePatients],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        highlightOnHover
        noRecordsText="Nessun paziente trovato"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'active',
            title: 'Attivo',
            width: '0%',
            render: (record) => (
              <Tooltip
                label={
                  !canEditUsers
                    ? "Non hai i permessi per modificare l'utente"
                    : record.user.activationTokenCreatedAt
                      ? "L'utente non ha ancora attivato il suo account"
                      : record.user.active
                        ? 'Disabilita'
                        : 'Abilita'
                }
              >
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    disabled={
                      !canEditUsers ||
                      record.user.activationTokenCreatedAt !== null ||
                      isLoadingUpdateUser
                    }
                    checked={record.user.active}
                    onChange={(e) => {
                      e.stopPropagation();
                      updateUser({
                        id: record.user.id,
                        body: { active: e.target.checked },
                      });
                    }}
                  />
                </div>
              </Tooltip>
            ),
          },
          {
            accessor: 'firstName',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  firstNameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setFirstNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={firstNameSearch}
                onChange={(e) => setFirstNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'lastName',
            title: 'Cognome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca cognome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  lastNameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setLastNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={lastNameSearch}
                onChange={(e) => setLastNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'title',
            title: 'Titolo',
          },
          {
            accessor: 'email',
            title: 'Email',
            filter: (
              <TextInput
                placeholder="Ricerca email"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  emailSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setEmailSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={emailSearch}
                onChange={(e) => setEmailSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'gender.name',
            title: 'Sesso',
            filter: (
              <GenderSelector
                multiple
                value={filters.genderIds}
                onChange={(value) =>
                  setFilters({ ...filters, genderIds: value || undefined })
                }
              />
            ),
          },
          {
            accessor: 'ethnicity.name',
            title: 'Etnia',
            filter: (
              <EthnicitySelector
                multiple
                value={filters.ethnicityIds}
                onChange={(value) =>
                  setFilters({ ...filters, ethnicityIds: value || undefined })
                }
              />
            ),
          },
          {
            accessor: 'pathologies',
            title: 'Patologie',
            render: (record) =>
              record.pathologies.map((p) => p.name).join(', '),
            filter: (
              <PathologiesSelector
                multiple
                value={filters.pathologiesIds}
                onChange={(value) =>
                  setFilters({ ...filters, pathologiesIds: value || undefined })
                }
              />
            ),
          },
          {
            accessor: 'medicalOrganization.user.name',
            title: 'Organizzazione medica',
            filter: (
              <MedicalOrganizationsSelector
                multiple
                value={filters.medicalOrganizationIds}
                onChange={(value) =>
                  setFilters({
                    ...filters,
                    medicalOrganizationIds: value || undefined,
                  })
                }
              />
            ),
          },
        ]}
        records={data}
        onRowClick={({ record }) => {
          navigate(`/pazienti/${record.id}`);
        }}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={patientsCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
